import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Plus, PartyPopper, Calendar, MapPin, Search } from "lucide-react";
import { getEvents, getSubPromoterEvents } from "../../services/eventService";
import Loader from "../../components/common/Loader";
import Button from "../../components/common/Button";
import Badge from "../../components/common/Badge";
import EmptyState from "../../components/common/EmptyState";
import EventLimitModal from "../../components/common/EventLimitModal";
import DraftPromptModal from "../../components/common/DraftPromptModal";
import { useCreateEventGate } from "../../hooks/useCreateEventGate";
import { toast } from "react-toastify";
import { color, radius, shadow } from "../../styles/theme";

const ROLE_FILTERS = ["all", "promoter", "subPromoter"];
const ORIGIN_FILTERS = ["all", "public", "nonPublic"];

const EventList = () => {
  const { t } = useTranslation(["events", "common"]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();
  // @Room's "Total Events" stat (RoomHomeHeader.jsx) links here wanting only
  // events this promoter actually created, not ones they're a sub-promoter
  // on — same distinction mobile's ARoomPromoter screen makes via its own
  // byUserEvents/artistUserId filter.
  const [roleFilter, setRoleFilter] = useState(ROLE_FILTERS.includes(location.state?.roleFilter) ? location.state.roleFilter : "all");
  const [originFilter, setOriginFilter] = useState("all");
  const [search, setSearch] = useState("");
  const {
    checking, openCreateEvent, limitInfo, closeLimitModal, sendLimitRequest, sendingRequest,
    draftPrompt, resumeDraft, discardDraftAndCreate, cancelDraftPrompt,
  } = useCreateEventGate();

  useEffect(() => {
    Promise.allSettled([getEvents(), getSubPromoterEvents()])
      .then(([ownResult, spResult]) => {
        const ownEvents = (ownResult.status === "fulfilled" ? ownResult.value.data?.Result : null) || [];
        const spEvents = (spResult.status === "fulfilled" ? spResult.value.data?.Result : null) || [];

        const ownIds = new Set(ownEvents.map((e) => e.Id));
        const taggedSpEvents = spEvents
          .filter((e) => !ownIds.has(e.Id))
          .map((e) => ({ ...e, _isAssigned: true }));

        setEvents([...ownEvents, ...taggedSpEvents]);

        if (ownResult.status === "rejected" && spResult.status === "rejected") {
          toast.error(t("eventList.toasts.loadFailed"));
        }
      })
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <Loader />;

  // Role: whether the account participates as the event's Promoter (owner)
  // or as an assigned Sub-Promoter — already known client-side from which
  // fetch(es) an event came from (see the merge above), no API support needed.
  // Event Type: Public/Non-Public, from Event.Origin (added to GetMyEvent's
  // response for this — GetMySubPromoterEvents already returned it).
  const searchTerm = search.trim().toLowerCase();
  const filteredEvents = events.filter((event) => {
    if (roleFilter === "promoter" && event._isAssigned) return false;
    if (roleFilter === "subPromoter" && !event._isAssigned) return false;
    if (originFilter === "public" && event.Origin !== "Public") return false;
    if (originFilter === "nonPublic" && (!event.Origin || event.Origin === "Public")) return false;
    if (searchTerm && !String(event.Name || "").toLowerCase().includes(searchTerm)) return false;
    return true;
  });

  return (
    <div>
      <div style={s.headerRow}>
        <h2 style={s.heading}>{t("eventList.heading")} <span style={s.count}>({filteredEvents.length})</span></h2>
        <Button icon={Plus} onClick={() => openCreateEvent("Public")} disabled={checking}>
          {checking ? t("eventList.checkingLabel") : t("eventList.createEventButton")}
        </Button>
      </div>

      {events.length > 0 && (
        <>
          <div style={s.searchRow}>
            <Search size={15} strokeWidth={2.25} style={s.searchIcon} />
            <input
              type="text"
              className="input"
              style={s.searchInput}
              placeholder={t("eventList.searchPlaceholder")}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <div style={s.filterRow}>
            <span style={s.filterLabel}>{t("eventList.originFilter.label")}</span>
            {ORIGIN_FILTERS.map((key) => (
              <button
                key={key}
                type="button"
                style={{ ...s.filterChip, ...(originFilter === key ? s.filterChipActive : {}) }}
                onClick={() => setOriginFilter(key)}
              >
                {t(`eventList.originFilter.options.${key}`)}
              </button>
            ))}
          </div>
          <div style={s.filterRow}>
            <span style={s.filterLabel}>{t("eventList.roleFilter.label")}</span>
            {ROLE_FILTERS.map((key) => (
              <button
                key={key}
                type="button"
                style={{ ...s.filterChip, ...(roleFilter === key ? s.filterChipActive : {}) }}
                onClick={() => setRoleFilter(key)}
              >
                {t(`eventList.roleFilter.options.${key}`)}
              </button>
            ))}
          </div>
        </>
      )}

      {events.length === 0 ? (
        <EmptyState icon={PartyPopper} text={t("eventList.emptyText")} />
      ) : filteredEvents.length === 0 ? (
        <EmptyState icon={PartyPopper} text={t("eventList.noMatchText")} />
      ) : (
        <div style={s.grid}>
          {filteredEvents.map((event) => (
            <div
              key={event.Id}
              className="icon-tile"
              style={s.card}
              onClick={() => navigate(`/events/${event.Id}`, { state: { isAssigned: !!event._isAssigned } })}
            >
              {event.Thumbnail ? (
                <img src={event.Thumbnail.replace("\\", "/")} alt={event.Name} style={s.thumbnail} />
              ) : (
                <div style={s.thumbnailPlaceholder}><PartyPopper size={36} strokeWidth={1.5} color={color.primary400} /></div>
              )}
              <div style={s.cardBody}>
                <div style={s.badgeRow}>
                  <Badge variant="primary">{event.EventType}</Badge>
                  {event._isAssigned && <Badge variant="warning">{t("eventList.subPromoterBadge")}</Badge>}
                </div>
                <div style={s.name}>{event.Name}</div>
                <div style={s.metaRow}><Calendar size={12} strokeWidth={2} /> {event.FormattedDate}</div>
                <div style={s.metaRow}><MapPin size={12} strokeWidth={2} /> {event.City}, {event.Country}</div>
              </div>
            </div>
          ))}
        </div>
      )}

      {limitInfo && (
        <EventLimitModal message={limitInfo.message} sending={sendingRequest} onClose={closeLimitModal} onSendRequest={sendLimitRequest} />
      )}
      {draftPrompt && (
        <DraftPromptModal onClose={cancelDraftPrompt} onResume={resumeDraft} onDiscard={discardDraftAndCreate} />
      )}
    </div>
  );
};

const s = {
  headerRow: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 },
  heading: { margin: 0, fontSize: 21, fontWeight: 800, color: color.textPrimary, letterSpacing: -0.4 },
  count: { fontSize: 15, fontWeight: 500, color: color.textMuted },
  searchRow: { position: "relative", maxWidth: 340, marginBottom: 16 },
  searchIcon: { position: "absolute", top: "50%", left: 12, transform: "translateY(-50%)", color: color.textMuted, pointerEvents: "none" },
  searchInput: { width: "100%", boxSizing: "border-box", paddingLeft: 36 },
  filterRow: { display: "flex", alignItems: "center", gap: 8, marginBottom: 20, flexWrap: "wrap" },
  filterLabel: { fontSize: 12.5, fontWeight: 600, color: color.textSecondary, marginRight: 4 },
  filterChip: {
    padding: "6px 14px", borderRadius: radius.pill, border: `1.5px solid ${color.border}`,
    background: color.gray50, color: color.gray700, fontWeight: 600, fontSize: 12.5, cursor: "pointer",
  },
  filterChipActive: { border: `1.5px solid ${color.primary500}`, background: color.primary50, color: color.primary600 },
  grid: { display: "flex", flexWrap: "wrap", gap: 18 },
  card: {
    background: color.surface, borderRadius: radius.lg, overflow: "hidden",
    boxShadow: shadow.sm, border: `1px solid ${color.border}`, cursor: "pointer", width: 280,
  },
  thumbnail: { width: "100%", height: 160, objectFit: "cover", display: "block" },
  thumbnailPlaceholder: { width: "100%", height: 160, display: "flex", alignItems: "center", justifyContent: "center", background: color.primary50 },
  cardBody: { padding: "15px 16px 17px" },
  badgeRow: { display: "flex", alignItems: "center", gap: 6, marginBottom: 10, flexWrap: "wrap" },
  name: { fontWeight: 700, fontSize: 15, color: color.textPrimary, marginBottom: 9 },
  metaRow: { display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: color.textSecondary, marginBottom: 4 },
};

export default EventList;
